/**
 * Admin — Smart Dispatch console.
 * Live grid of active offer-chains + per-booking timeline of attempts.
 * Auto-refreshes every 3 seconds. Actions: cancel, force-assign.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, X, Check, Users, MapPin, Clock,
  AlertCircle, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REFRESH_MS = 3000;

function fmtTime(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString("hr-HR"); } catch { return iso; }
}

function offerCountdown(offer) {
  if (!offer?.expires_at) return null;
  const s = Math.max(0,
    Math.floor((new Date(offer.expires_at).getTime() - Date.now()) / 1000));
  return s;
}

export default function AdminDispatch() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [showForceFor, setShowForceFor] = useState(null);
  const [candidates, setCandidates] = useState({ rows: [] });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/smart-dispatch/active");
      setData(data);
    } catch {
      toast.error("Failed to load active dispatches");
    }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, REFRESH_MS);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(poll); clearInterval(t); };
  }, []);

  const cancel = async (bookingId) => {
    if (!window.confirm("Cancel dispatch for this booking?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${bookingId}/smart-dispatch/cancel`);
      toast.success("Cancelled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const openForce = async (bookingId) => {
    setShowForceFor(bookingId);
    setCandidates({ rows: [] });
    try {
      const { data } = await api.get(`/admin/smart-dispatch/candidates/${bookingId}`);
      setCandidates(data);
    } catch {
      toast.error("Failed to load candidates");
    }
  };

  const force = async (bookingId, driverId) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${bookingId}/smart-dispatch/force`,
                        { driver_id: driverId });
      toast.success("Assigned");
      setShowForceFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-assign failed");
    } finally { setBusy(false); }
  };

  const totalActive = data.rows?.length || 0;

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="admin-dispatch">
      <header className="border-b border-white/10 sticky top-0 z-20 backdrop-blur-md bg-black/70">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <p className="font-display text-lg text-white">Smart Dispatch</p>
            <p className="text-[11px] text-zinc-500">Live offer-chain monitor · 30s per attempt</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Active</p>
            <p className="font-display text-2xl text-[#C5A059] tabular-nums"
               data-testid="dispatch-active-count">{totalActive}</p>
          </div>
          <button onClick={load} disabled={busy}
                  data-testid="dispatch-refresh-btn"
                  className="h-10 px-4 rounded-full border border-white/15 text-zinc-300 text-[10px] uppercase tracking-widest hover:text-white hover:border-white/30 flex items-center gap-2 disabled:opacity-40">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {totalActive === 0 ? (
          <div className="text-center py-24" data-testid="dispatch-empty">
            <Zap className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">No active dispatches right now.</p>
            <p className="text-zinc-500 text-xs mt-2">
              Bookings appear here automatically when their pickup is within 60 minutes.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.rows.map((row) => (
              <DispatchRow key={row.booking.id} row={row}
                            onCancel={() => cancel(row.booking.id)}
                            onForce={() => openForce(row.booking.id)}
                            tick={tick} />
            ))}
          </div>
        )}
      </main>

      {showForceFor && (
        <ForceModal
          bookingId={showForceFor}
          candidates={candidates.rows}
          busy={busy}
          onClose={() => setShowForceFor(null)}
          onPick={(did) => force(showForceFor, did)}
        />
      )}
    </div>
  );
}

function DispatchRow({ row, onCancel, onForce, tick }) {
  const { booking, current_offer, history } = row;
  // tick forces the countdown to refresh
  const countdown = current_offer ? offerCountdown(current_offer) : null;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5"
         data-testid={`dispatch-row-${booking.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-display text-lg text-white truncate">
              {booking.name || booking.ref || booking.id}
            </p>
            <span className="text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">
              Searching · {booking.dispatch_attempts || 0} tried
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {booking.pickup_location} → {booking.dropoff_location || "-"}
          </p>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {booking.pickup_date} · {booking.pickup_time}
            {booking.quote_amount ? ` · €${Number(booking.quote_amount).toFixed(2)}` : ""}
          </p>
        </div>

        {/* Current offer summary */}
        {current_offer ? (
          <div className="text-right shrink-0 min-w-[160px]"
               data-testid={`dispatch-offer-${booking.id}`}>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Offered to</p>
            <p className="text-sm text-white">{current_offer.driver_name}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {current_offer.distance_km} km · {current_offer.gps_freshness}
            </p>
            <p className="font-display text-3xl text-emerald-400 tabular-nums mt-1">
              {countdown != null ? `${countdown}s` : "—"}
            </p>
          </div>
        ) : (
          <div className="text-right shrink-0 flex items-center gap-2 text-orange-300">
            <AlertCircle className="w-4 h-4" />
            <p className="text-xs">Between attempts…</p>
          </div>
        )}
      </div>

      {/* Attempt history */}
      {history?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2"
             data-testid={`dispatch-history-${booking.id}`}>
          {history.map((h, ix) => (
            <span key={`${h.driver_name}-${ix}`}
                  className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                    h.status === "declined"    ? "border-red-500/30 text-red-300"
                      : h.status === "timeout" ? "border-orange-500/30 text-orange-300"
                      : h.status === "superseded" ? "border-white/10 text-zinc-500"
                      : h.status === "cancelled" ? "border-zinc-500/30 text-zinc-400"
                      : "border-white/10 text-zinc-400"
                  }`}>
              {h.driver_name}: {h.status}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-2 justify-end">
        <button onClick={onCancel}
                data-testid={`dispatch-cancel-${booking.id}`}
                className="h-9 px-4 rounded-full border border-red-500/30 text-red-300 text-[10px] uppercase tracking-widest hover:bg-red-500/10 flex items-center gap-1">
          <X className="w-3 h-3" /> Cancel
        </button>
        <button onClick={onForce}
                data-testid={`dispatch-force-${booking.id}`}
                className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
          <Users className="w-3 h-3" /> Force-assign
        </button>
      </div>
    </div>
  );
}

function ForceModal({ bookingId, candidates, busy, onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
         onClick={onClose}
         data-testid="force-modal">
      <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="font-display text-lg text-white">Force-assign driver</p>
            <p className="text-[11px] text-zinc-500">{bookingId}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {candidates.length === 0 ? (
            <p className="text-zinc-500 text-xs">No eligible drivers.</p>
          ) : candidates.map((c) => (
            <button key={c.driver_id}
                    disabled={busy}
                    onClick={() => onPick(c.driver_id)}
                    data-testid={`force-pick-${c.driver_id}`}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:bg-white/[0.02] transition-colors text-left disabled:opacity-40">
              <div>
                <p className="text-sm text-white">{c.driver_name}</p>
                <p className="text-[11px] text-zinc-500">
                  {c.distance_km} km · {c.gps_freshness} GPS · 7d gross €{c.trailing_7d_gross_eur}
                </p>
              </div>
              <span className="font-mono text-[11px] text-emerald-400 tabular-nums">
                {(c.score * 100).toFixed(1)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
